import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import jsPDF from "jspdf";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Boxes, Check, Plus, RotateCcw, Share2, X, FileImage, FileText,
  Sparkles, ExternalLink, Loader2, ImageOff,
} from "lucide-react";
import { SHOWROOM } from "@/constants/testIds";
import {
  DOOR_STYLES, DOOR_SWATCH, CABINET_COLORS, DEFAULT_COLOR_FOR_STYLE,
  WALL_TINTS, TEMPLATES, TEMPLATE_LIST, APPLIANCE_DEPOT_URL, SCENE_FILE,
} from "@/showroom/layout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PRODUCT_IMG = (id) => `${BACKEND_URL}/api/static/products/${id}.png`;
// Prefer the SKU-specific studio photo (same image Nano Banana uses as its
// visual reference when compositing) so the picker card matches the rendered
// appliance exactly. Falls back to the generic bucket if the SKU ref hasn't
// been generated yet.
const APPLIANCE_REF_IMG = (modelId, fallbackImageBucket) => {
  if (!modelId) return PRODUCT_IMG(fallbackImageBucket);
  return `${BACKEND_URL}/api/static/appliance_refs/${modelId}.png`;
};
const SCENE_IMG   = (file) => `${BACKEND_URL}/api/static/scenes/${file}`;

// SEC-002: only allow http(s) links to leak out of the widget. Anything else
// (`javascript:`, `data:`, `vbscript:` …) falls back to the general appliance
// category page on cabinetdepot.com.
const APPLIANCE_DEPOT_FALLBACK = "https://cabinetdepot.com/appliances";
function safeShopUrl(rawUrl, slotToCategory, slot) {
  const category = (slotToCategory && slotToCategory[slot]) || "appliances";
  const fallback = `https://cabinetdepot.com/${category}`;
  if (!rawUrl || typeof rawUrl !== "string") return fallback;
  const trimmed = rawUrl.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return fallback;
}
void APPLIANCE_DEPOT_FALLBACK;

/**
 * Read theme.* URL params and return a CSS string that overrides the widget's
 * highest-visibility surfaces. Returns null if no theme params are present.
 *
 * Accepted params (all optional, hex colors; `#` may be URL-encoded):
 *   themeBg          — page background
 *   themeSurface     — cards / toolbars / picker modals (default: white)
 *   themeText        — primary text color
 *   themeMuted       — secondary/label text color
 *   themeBorder      — border and divider color
 *   themePrimary     — primary button background (Share, Next, hotspots)
 *   themePrimaryText — text on primary buttons
 *   themeAccent      — accent color for kickers / active steps
 *   themeRadius      — corner radius for buttons and cards (integer, px)
 *   themeFont        — CSS font-family stack (e.g. "Inter, sans-serif")
 */
const HEX_COLOR = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
const cssColor = (raw) => {
  if (!raw) return null;
  let v = String(raw).trim();
  if (!v.startsWith("#")) v = "#" + v;
  return HEX_COLOR.test(v) ? v : null;
};

// XSS hardening: `themeFont` is the only user-supplied string interpolated
// into the `<style>` tag, so restrict it to a strict allow-list of
// font-family-safe characters. Anything else (`<`, `>`, `{`, `}`, `;`, `"`,
// `'`, `\`, `/`, backticks, whitespace-newline) is rejected — closing the
// door on `themeFont=A;} </style><script>...` breakout attacks.
const SAFE_FONT_STACK = /^[A-Za-z0-9 ,\-_.]+$/;
const cssFontStack = (raw) => {
  if (!raw) return null;
  const v = String(raw).trim().slice(0, 120);
  return v && SAFE_FONT_STACK.test(v) ? v : null;
};
function buildThemeCss(params) {
  const g = (k) => cssColor(params.get(k));
  const bg          = g("themeBg");
  const surface     = g("themeSurface");
  const text        = g("themeText");
  const muted       = g("themeMuted");
  const border      = g("themeBorder");
  const primary     = g("themePrimary");
  const primaryText = g("themePrimaryText");
  const accent      = g("themeAccent");
  const introBg     = g("themeIntroBg");
  const introText   = g("themeIntroText");
  const rawRadius   = parseInt(params.get("themeRadius") || "", 10);
  const radius      = Number.isFinite(rawRadius) && rawRadius >= 0 && rawRadius <= 40 ? `${rawRadius}px` : null;
  const font        = cssFontStack(params.get("themeFont"));

  if (![bg, surface, text, muted, border, primary, primaryText, accent, introBg, introText, radius, font].some(Boolean)) {
    return null;
  }

  const root = `[data-kf-themed="1"]`;
  const lines = [];
  const vars  = [];
  if (bg)          vars.push(`--kf-bg: ${bg};`);
  if (surface)     vars.push(`--kf-surface: ${surface};`);
  if (text)        vars.push(`--kf-text: ${text};`);
  if (muted)       vars.push(`--kf-muted: ${muted};`);
  if (border)      vars.push(`--kf-border: ${border};`);
  if (primary)     vars.push(`--kf-primary: ${primary};`);
  if (primaryText) vars.push(`--kf-primary-text: ${primaryText};`);
  if (accent)      vars.push(`--kf-accent: ${accent};`);
  if (introBg)     vars.push(`--kf-intro-bg: ${introBg};`);
  if (introText)   vars.push(`--kf-intro-text: ${introText};`);
  if (radius)      vars.push(`--kf-radius: ${radius};`);
  if (font)        vars.push(`--kf-font: ${font};`);
  lines.push(`${root} { ${vars.join(" ")} }`);

  // Page background surfaces (Showroom root + wizard step backgrounds)
  if (bg) {
    lines.push(`${root}.min-h-screen, ${root} .bg-\\[\\#F8FAFC\\] { background-color: var(--kf-bg) !important; }`);
    lines.push(`${root} .bg-slate-100 { background-color: color-mix(in srgb, var(--kf-bg) 96%, black) !important; }`);
    lines.push(`${root} .bg-slate-50  { background-color: var(--kf-bg) !important; }`);
  }

  // Cards / toolbars / modals
  if (surface) {
    lines.push(`${root} .bg-white { background-color: var(--kf-surface) !important; }`);
  }

  // Borders (dividers between wizard cards, toolbar bottom border, etc.)
  if (border) {
    lines.push(`${root} .border-slate-200, ${root} .border-slate-100, ${root} .border-slate-300 { border-color: var(--kf-border) !important; }`);
    lines.push(`${root} .divide-slate-200 > * + * { border-color: var(--kf-border) !important; }`);
  }

  // Text colors
  if (text) {
    lines.push(`${root} .text-slate-900, ${root} .text-slate-800, ${root} .text-black { color: var(--kf-text) !important; }`);
  }
  if (muted) {
    lines.push(`${root} .text-slate-500, ${root} .text-slate-600, ${root} .text-slate-400, ${root} .text-slate-700 { color: var(--kf-muted) !important; }`);
  }

  // Primary buttons — the dark Share / Next / hotspot buttons
  if (primary) {
    const primaryHover = `color-mix(in srgb, var(--kf-primary) 85%, black)`;
    lines.push(`${root} .bg-slate-900, ${root} .bg-slate-800, ${root} .hover\\:bg-slate-900:hover, ${root} .hover\\:bg-slate-800:hover { background-color: var(--kf-primary) !important; }`);
    lines.push(`${root} .hover\\:bg-slate-800:hover { background-color: ${primaryHover} !important; }`);
    // Small colored circles like the "+" hotspot buttons
    lines.push(`${root} .bg-blue-500, ${root} .bg-blue-600 { background-color: var(--kf-primary) !important; }`);
    lines.push(`${root} .ring-blue-500, ${root} .ring-blue-500\\/60 { --tw-ring-color: var(--kf-primary) !important; }`);
    lines.push(`${root} .border-blue-500, ${root} .border-blue-400 { border-color: var(--kf-primary) !important; }`);
  }
  if (primaryText) {
    lines.push(`${root} .bg-slate-900, ${root} .bg-slate-800 { color: var(--kf-primary-text) !important; }`);
    lines.push(`${root} .bg-blue-500, ${root} .bg-blue-600 { color: var(--kf-primary-text) !important; }`);
  }

  // Accent — the amber/gold "kicker" labels and step-active glow
  if (accent) {
    lines.push(`${root} .text-amber-300, ${root} .text-amber-400, ${root} .text-amber-500, ${root} .text-orange-500, ${root} .text-orange-600 { color: var(--kf-accent) !important; }`);
    lines.push(`${root} .bg-amber-300, ${root} .bg-amber-400, ${root} .bg-amber-500 { background-color: var(--kf-accent) !important; }`);
    lines.push(`${root} .ring-amber-400, ${root} .ring-amber-300 { --tw-ring-color: var(--kf-accent) !important; }`);
  }

  // Radius on buttons + cards
  if (radius) {
    lines.push(`${root} .rounded-md, ${root} .rounded-lg, ${root} .rounded-xl, ${root} .rounded-2xl, ${root} button, ${root} .rounded { border-radius: var(--kf-radius) !important; }`);
  }

  // Font family
  if (font) {
    lines.push(`${root}, ${root} button, ${root} input, ${root} textarea, ${root} select, ${root} .font-display { font-family: var(--kf-font) !important; }`);
  }

  // ---- Intro hero ("main page") --------------------------------------------
  // The hero uses hard-coded colors in inline styles; theme it via a scoped
  // selector so overrides don't bleed into the wizard steps below.
  if (introText) {
    lines.push(`${root} [data-kf-intro="1"] .text-white { color: var(--kf-intro-text) !important; }`);
    // Muted text on the hero should stay readable against a light hero bg —
    // fall back to 70% and 55% of the primary hero text.
    lines.push(`${root} [data-kf-intro="1"] .text-slate-300 { color: color-mix(in srgb, var(--kf-intro-text) 78%, transparent) !important; }`);
    lines.push(`${root} [data-kf-intro="1"] .text-slate-400 { color: color-mix(in srgb, var(--kf-intro-text) 60%, transparent) !important; }`);
    // Ensure the number pill on step tiles remains legible: use themePrimary if
    // available, else the primary hero text color, so it doesn't get lost.
    lines.push(`${root} [data-kf-intro="1"] .text-slate-900 { color: var(--kf-primary, var(--kf-intro-text)) !important; }`);
    // Frosted step cards default to a soft white overlay — nudge toward the
    // intro text color so they stay visible on either dark or light heroes.
    lines.push(`${root} [data-kf-intro="1"] .bg-white\\/5 { background-color: color-mix(in srgb, var(--kf-intro-text) 8%, transparent) !important; }`);
    lines.push(`${root} [data-kf-intro="1"] .border-white\\/10 { border-color: color-mix(in srgb, var(--kf-intro-text) 18%, transparent) !important; }`);
    // Logo panel background should also adapt (was `bg-slate-900/70`)
    lines.push(`${root} [data-kf-intro="1"] .bg-slate-900\\/70 { background-color: color-mix(in srgb, var(--kf-intro-text) 15%, transparent) !important; }`);
  }

  return lines.join("\n");
}

/**
 * Fetch the live Cabinet Depot appliance catalog from our /api/appliances
 * proxy (which pulls from Base44 and normalizes into fridge/range/dishwasher/
 * wine slot groups). Returns { loading, error, slots, retry } — an `error`
 * lets the UI show a "Catalog unavailable" banner with a Retry button
 * instead of silently rendering an empty picker.
 */
function useLiveAppliances() {
  const [state, setState] = useState({ loading: true, error: null, slots: {} });
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/appliances`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        if (!cancelled) setState({ loading: false, error: null, slots: d.slots || {} });
      } catch (e) {
        if (!cancelled) setState({ loading: false, error: e?.message || "network", slots: {} });
      }
    })();
    return () => { cancelled = true; };
  }, [nonce]);
  return { ...state, retry: () => setNonce((n) => n + 1) };
}

// Convert a live-catalog record into the picker-card shape the UI expects.
function toModelCard(a) {
  return {
    id:        a.id,
    slot:      a.slot,
    brand:     a.brand || "Cabinet Depot",
    name:      a.title || a.description || a.model || "Untitled",
    sku:       a.model || a.id,
    size:      [a.width, a.depth, a.height].filter(Boolean).join(" × ") || "—",
    finish:    a.color || "—",
    // Absolute URL for the picker card image
    image_url: a.image_url ? `${BACKEND_URL}${a.image_url}` : null,
    // Server-side selection payload — sent verbatim to /api/showroom/composite
    _selection: {
      id:          a.id,
      slot:        a.slot,
      brand:       a.brand,
      model:       a.model,
      title:       a.title,
      description: a.description,
      color:       a.color,
      image_url:   a.image_url,
    },
    source_url: a.source_url,
  };
}

/**
 * Debounced fetch of the AI-composited scene. Fires when the user picks a
 * non-default color OR at least one appliance — otherwise the base scene is
 * shown directly. Handles transient CDN / rate-limit / 5xx errors with a
 * small exponential-backoff auto-retry so shoppers don't lose their design
 * to a one-off blip, and exposes { error, retry, startedAt } so the UI can
 * show elapsed time and a manual "Try again" button if all retries fail.
 */
function useCompositeScene({ layout, doorStyle, color, wallColor, selections }) {
  const defaultColor = DEFAULT_COLOR_FOR_STYLE[doorStyle];
  const [url, setUrl]         = useState(null);
  const [loading, setLoading] = useState(false);
  const [cached, setCached]   = useState(false);
  const [error, setError]     = useState(null);
  const [startedAt, setStarted] = useState(null);
  const [manualRetry, setManualRetry] = useState(0);

  const activeColor = color && color !== defaultColor ? color : null;
  const activeWall  = wallColor && wallColor !== "off_white" ? wallColor : null;

  const key = useMemo(() => {
    const filled = Object.entries(selections).filter(([, v]) => !!v);
    if (!activeColor && !activeWall && filled.length === 0) return null;
    filled.sort(([a], [b]) => a.localeCompare(b));
    return JSON.stringify({
      l: layout,
      d: doorStyle,
      c: activeColor || "",
      w: activeWall  || "",
      s: Object.fromEntries(
        filled.map(([slotId, sel]) => [
          slotId,
          typeof sel === "string" ? sel : (sel && sel._selection) || sel,
        ]),
      ),
    });
  }, [layout, doorStyle, activeColor, activeWall, selections]);

  useEffect(() => {
    // Keep the last successful URL visible while a new render kicks off so
    // the scene doesn't flash back to the plain base layout on every edit.
    setError(null);
    if (!key) { setLoading(false); setUrl(null); return; }
    let cancelled = false;
    setLoading(true);
    setStarted(Date.now());

    const attempt = async (tryNum) => {
      try {
        const body = JSON.parse(key);
        const res  = await fetch(`${BACKEND_URL}/api/showroom/composite`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body:   JSON.stringify({
            layout:     body.l,
            door_style: body.d,
            color:      body.c || null,
            wall_color: body.w || null,
            selections: body.s,
          }),
        });
        if (!res.ok) {
          const text = await res.text().catch(() => "");
          const err  = new Error(text || `HTTP ${res.status}`);
          err.status = res.status;
          err.retryable = res.status >= 500 || res.status === 429 || res.status === 408;
          throw err;
        }
        const data = await res.json();
        if (cancelled) return;
        setUrl(`${BACKEND_URL}${data.image_url}`);
        setCached(data.cached);
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        const isNetwork = e?.name === "TypeError";
        if ((e?.retryable || isNetwork) && tryNum < 3) {
          setTimeout(() => { if (!cancelled) attempt(tryNum + 1); }, 2000 * tryNum);
          return;
        }
        setLoading(false);
        setError(e?.message || "Render failed");
        console.warn("composite render failed", e);
      }
    };

    const t = setTimeout(() => attempt(1), 450);
    return () => { cancelled = true; clearTimeout(t); };
  }, [key, manualRetry]);
  // `BACKEND_URL` is a module-level constant (baked from CRA env at build
  // time), so it can't change at runtime — intentionally omitted from deps.

  return {
    url, loading, cached, error, startedAt,
    retry: () => setManualRetry((n) => n + 1),
  };
}

/**
 * Product photo with a graceful fallback state. Renders a subtle placeholder
 * (icon + brand name) instead of the browser's broken-image icon when:
 *   • `src` is null/empty (Base44 record has no image)
 *   • the URL fetch fails (403/404/mixed content)
 *   • `fallbackSrc` also fails
 * The state resets whenever `src` changes so the next card in a virtualised
 * list doesn't inherit the previous card's failure.
 */
function AppliancePhoto({ src, fallbackSrc, alt, brand, className }) {
  const [current, setCurrent] = useState(src || fallbackSrc || null);
  const [failed, setFailed]   = useState(false);
  const triedFallbackRef      = useRef(false);
  useEffect(() => {
    setCurrent(src || fallbackSrc || null);
    setFailed(!(src || fallbackSrc));
    triedFallbackRef.current = false;
  }, [src, fallbackSrc]);

  if (failed || !current) {
    return (
      <div className={`w-full h-full flex flex-col items-center justify-center text-slate-400 select-none ${className || ""}`}>
        <ImageOff size={32} strokeWidth={1.5} />
        {brand && (
          <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-slate-500 text-center px-2 line-clamp-1">
            {brand}
          </div>
        )}
        <div className="text-[9px] font-mono text-slate-400 mt-0.5">No photo</div>
      </div>
    );
  }

  return (
    <img
      src={current}
      alt={alt || ""}
      className={className}
      loading="lazy"
      decoding="async"
      onError={() => {
        if (!triedFallbackRef.current && fallbackSrc && current !== fallbackSrc) {
          triedFallbackRef.current = true;
          setCurrent(fallbackSrc);
        } else {
          setFailed(true);
        }
      }}
    />
  );
}

export default function Showroom({ embed = false } = {}) {
  const [params, setParams] = useSearchParams();

  // Restore last session from localStorage when the URL has no state params.
  // Guards against accidental refreshes / tab-crashes so shoppers don't lose
  // their design. Wrapped in try/catch because iframe sandboxing sometimes
  // blocks localStorage access.
  const stored = useMemo(() => {
    try {
      const hasUrlState = ["layout","door","color","wall","s"].some((k) => params.get(k));
      if (hasUrlState) return null;
      const raw = window.localStorage.getItem("kf.showroom.v1");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
    // Only read on the first mount — subsequent renders should NOT re-hydrate
    // from storage or they'd fight the user's edits.
  }, []);

  const [layoutId,  setLayoutId]  = useState(params.get("layout")  || stored?.layoutId  || "galley");
  const [doorStyle, setDoorStyle] = useState(params.get("door")    || stored?.doorStyle || "essex");
  const [colorId,   setColorId]   = useState(params.get("color")   || stored?.colorId   || DEFAULT_COLOR_FOR_STYLE.essex);
  const [wallId,    setWallId]    = useState(params.get("wall")    || stored?.wallId    || "off_white");

  const template = TEMPLATES[layoutId] || TEMPLATES.galley;
  const style    = DOOR_STYLES.find((s) => s.id === doorStyle) || DOOR_STYLES[0];
  const color    = CABINET_COLORS.find((c) => c.id === colorId) || CABINET_COLORS[0];
  const wall     = WALL_TINTS.find((w) => w.id === wallId) || WALL_TINTS[0];

  const [selections, setSelections] = useState(() => {
    // Selections are hydrated later from the URL once the live Base44 catalog
    // has loaded (see the sync-in effect below). Fall back to whatever was
    // saved in localStorage on the previous session so a hard refresh
    // doesn't wipe the design.
    if (stored?.selections && typeof stored.selections === "object") {
      return stored.selections;
    }
    return {};
  });
  const [pickerSlot, setPickerSlot] = useState(null);
  const [brandFilter, setBrandFilter] = useState("all");
  const [shareOpen,  setShareOpen]  = useState(false);
  const [shareUrl,   setShareUrl]   = useState("");
  // Wizard step: 0 = intro, 1 = layout, 2 = cabinets, 3 = appliances, 4 = final render
  const initialStep = (() => {
    const raw = parseInt(params.get("step") || "0", 10);
    return Number.isFinite(raw) && raw >= 0 && raw <= 4 ? raw : 0;
  })();
  const [step, setStep] = useState(initialStep);
  const sceneRef = useRef(null);
  const liveCatalog = useLiveAppliances();

  // Track whether we've had a chance to hydrate `selections` from the URL's
  // `s=` param. On first mount `selections` is empty, so without this guard
  // the sync-out effect below would wipe `s=` from the URL before the live
  // catalog finishes loading, causing shared links to lose their appliances.
  const hydratedRef = useRef(false);

  // Rehydrate `selections` from the URL once the live catalog is ready.
  // Format: `s=fridge:<uuid>,range:<uuid>` — only re-runs when the catalog
  // finishes loading or the raw URL selection string changes.
  const urlSelectionsRaw = params.get("s") || "";
  useEffect(() => {
    if (liveCatalog.loading) return;
    if (urlSelectionsRaw) {
      const validSlots = new Set((TEMPLATES[layoutId] || TEMPLATES.galley).slots.map((s) => s.id));
      const next = {};
      urlSelectionsRaw.split(",").forEach((pair) => {
        const [slot, id] = pair.split(":");
        if (!slot || !id || !validSlots.has(slot)) return;
        const pool = liveCatalog.slots[slot] || [];
        const match = pool.find((a) => a.id === id);
        if (match) next[slot] = toModelCard(match);
      });
      if (Object.keys(next).length) setSelections((prev) => ({ ...next, ...prev }));
    }
    // Whether or not there was anything to hydrate, we've now had our chance
    // — release the sync-out gate so future edits write the URL as usual.
    hydratedRef.current = true;
  }, [liveCatalog.loading, urlSelectionsRaw, layoutId, liveCatalog.slots]);

  // -----------------------------------------------------------------------
  // Parent-page messaging API (for sites that embed KitchenForge in an iframe
  // and want to drive it from a package picker without reloading the frame).
  //
  // Parent → iframe:
  //   iframe.contentWindow.postMessage({
  //     type: "kf:applyPackage",
  //     selections: { fridge: "<base44_id>", range: "<id>", dishwasher: "<id>", wine: "<id>" },
  //     // Optional surface overrides:
  //     layout: "galley" | "compact" | "lshape",
  //     doorStyle: "essex" | "windsor" | "rockingham",
  //     color: "white" | "sage" | ...,
  //     wall: "off_white" | "warm" | "terracotta" | ...,
  //     step: 1..4,   // jump the shopper straight to the preview
  //     reset: true,  // start fresh, ignoring any existing picks
  //   }, "*");
  //
  // Iframe → parent (fire-and-forget):
  //   { type: "kf:ready", version: 1 }                 // emitted on mount
  //   { type: "kf:selectionsChanged", selections: {}, layout, doorStyle, color, wall }
  //                                                     // emitted on every design edit
  //
  // Any unknown fields are ignored; invalid appliance IDs are silently dropped
  // (same validation as the URL `s=` param — bad IDs can never crash the app).
  useEffect(() => {
    const onMessage = (ev) => {
      const data = ev?.data;
      if (!data || typeof data !== "object" || data.type !== "kf:applyPackage") return;
      if (data.reset) setSelections({});
      if (typeof data.layout    === "string" && TEMPLATES[data.layout])     setLayoutId(data.layout);
      if (typeof data.doorStyle === "string" && DOOR_STYLES.find((s) => s.id === data.doorStyle)) setDoorStyle(data.doorStyle);
      if (typeof data.color     === "string" && CABINET_COLORS.find((c) => c.id === data.color))  setColorId(data.color);
      if (typeof data.wall      === "string" && WALL_TINTS.find((w) => w.id === data.wall))       setWallId(data.wall);
      if (Number.isFinite(data.step) && data.step >= 0 && data.step <= 4) setStep(data.step);

      // Resolve the appliance IDs against the live catalog so we hand the
      // scene the full model card (brand, title, image_url) — not just an id.
      if (data.selections && typeof data.selections === "object") {
        const validSlots = new Set((TEMPLATES[data.layout || layoutId] || TEMPLATES.galley).slots.map((s) => s.id));
        const next = {};
        Object.entries(data.selections).forEach(([slot, id]) => {
          if (!validSlots.has(slot) || typeof id !== "string") return;
          const pool  = liveCatalog.slots?.[slot] || [];
          const match = pool.find((a) => a.id === id);
          if (match) next[slot] = toModelCard(match);
        });
        if (Object.keys(next).length) {
          setSelections((prev) => ({ ...(data.reset ? {} : prev), ...next }));
        }
      }
    };
    window.addEventListener("message", onMessage);
    // Tell the parent page we're ready to receive packages.
    try { window.parent?.postMessage({ type: "kf:ready", version: 1 }, "*"); } catch { /* iframe sandbox */ }
    return () => window.removeEventListener("message", onMessage);
    // `layoutId` is read inside the handler for the validSlots fallback — we
    // only need the current value at call time, not re-subscribe when it
    // changes (would detach the listener briefly on every edit).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveCatalog.slots]);

  // Broadcast selection changes to the parent so the host page can light up
  // the active package pill, show a running cart total, etc.
  useEffect(() => {
    try {
      window.parent?.postMessage({
        type: "kf:selectionsChanged",
        selections: Object.fromEntries(
          Object.entries(selections)
            .filter(([, v]) => v && typeof v === "object" && v.id)
            .map(([slot, v]) => [slot, v.id]),
        ),
        layout: layoutId,
        doorStyle,
        color: colorId,
        wall: wallId,
      }, "*");
    } catch { /* iframe sandbox */ }
  }, [selections, layoutId, doorStyle, colorId, wallId]);

  // Sync all state (layout, style, colors, appliance selections, step) → URL.
  // Selections are encoded as `s=<slot>:<base44_id>,<slot>:<base44_id>` so
  // shared links restore the exact appliance choices when the live catalog
  // finishes loading (see rehydration effect above).
  useEffect(() => {
    const next = new URLSearchParams();
    next.set("layout", layoutId);
    next.set("door",   doorStyle);
    next.set("color",  colorId);
    next.set("wall",   wallId);
    if (step > 0) next.set("step", String(step));
    const encoded = Object.entries(selections)
      .filter(([, v]) => v && typeof v === "object" && v.id)
      .map(([slot, v]) => `${slot}:${v.id}`)
      .join(",");
    if (encoded) {
      next.set("s", encoded);
    } else if (!hydratedRef.current && urlSelectionsRaw) {
      // Catalog still loading (or hydration hasn't run) — keep the incoming
      // `s=` string verbatim so shared links don't lose their appliances.
      next.set("s", urlSelectionsRaw);
    }
    // Preserve embed-side hints so Share URLs stay correct across state changes.
    const preservedShareBase   = params.get("shareBase");
    const preservedShareOrigin = params.get("shareOrigin");
    if (preservedShareBase)   next.set("shareBase",   preservedShareBase);
    if (preservedShareOrigin) next.set("shareOrigin", preservedShareOrigin);
    // Preserve embed theme params so the widget stays styled after each edit.
    [
      "themeBg", "themeSurface", "themeText", "themeMuted", "themeBorder",
      "themePrimary", "themePrimaryText", "themeAccent",
      "themeIntroBg", "themeIntroText",
      "themeRadius", "themeFont",
    ].forEach((k) => {
      const v = params.get(k);
      if (v) next.set(k, v);
    });
    setParams(next, { replace: true });
  }, [layoutId, doorStyle, colorId, wallId, selections, step, setParams, params, urlSelectionsRaw]);

  // Persist the design to localStorage on every change so a hard refresh or
  // tab crash doesn't wipe the shopper's work. Only writes plain JSON — the
  // sync-in effect above already validates ids against the catalog on next
  // load, so a stale storage entry can't crash the app.
  useEffect(() => {
    try {
      window.localStorage.setItem("kf.showroom.v1", JSON.stringify({
        layoutId, doorStyle, colorId, wallId, selections,
      }));
    } catch { /* storage blocked in some iframe sandboxes — safe to ignore */ }
  }, [layoutId, doorStyle, colorId, wallId, selections]);

  // When door style changes, reset color to that style's default (if user hasn't picked one)
  useEffect(() => {
    if (!params.get("color")) setColorId(DEFAULT_COLOR_FOR_STYLE[doorStyle] || "white");
  }, [doorStyle, params]);

  // Prune stale selections when layout changes
  useEffect(() => {
    setSelections((prev) => {
      const valid = new Set(template.slots.map((s) => s.id));
      const filtered = {};
      Object.entries(prev).forEach(([k, v]) => { if (valid.has(k)) filtered[k] = v; });
      return filtered;
    });
  }, [layoutId, template.slots]);

  // Reset brand filter each time the picker opens for a new slot so users
  // don't inherit e.g. "Whirlpool" from the fridge picker into the range picker.
  useEffect(() => { setBrandFilter("all"); }, [pickerSlot]);

  const chooseModel = (slotId, modelCard) => {
    // Store the whole normalized card so hotspots + summary + composite fetch
    // all see the same live catalog record.
    setSelections((prev) => ({ ...prev, [slotId]: modelCard }));
    setPickerSlot(null);
    toast.success("Added to your kitchen", { duration: 1500 });
  };

  const removeSlot = (slotId) => {
    setSelections((prev) => {
      const next = { ...prev };
      delete next[slotId];
      return next;
    });
  };

  const reset = () => {
    if (!Object.keys(selections).length) return;
    if (window.confirm("Clear all appliance selections?")) {
      setSelections({});
      try { window.localStorage.removeItem("kf.showroom.v1"); } catch { /* iframe sandbox */ }
    }
  };

  const share = async () => {
    // When the widget is embedded on a partner site (e.g. cabinetdepot.com),
    // `window.location.href` is the iframe's URL — not what the user wants to
    // share. Priority for the shareable link:
    //   1. `?shareBase=<url>` on the /embed URL — the sharable landing page on
    //      the partner site. State is appended so opening the link on that
    //      partner site restores the exact configuration (the partner page
    //      forwards the query into the iframe).
    //   2. `?shareOrigin=<origin>` — same path but under a different origin.
    //   3. `REACT_APP_BACKEND_URL` (our canonical public domain) — auto-swap
    //      the origin so a widget served from `*.emergent.host` still shares
    //      links on the app's real domain (`designshowcase.appliancedepot.ai`).
    //   4. Final fallback: raw `window.location.href`.
    const shareBase   = params.get("shareBase");
    const shareOrigin = params.get("shareOrigin") || BACKEND_URL;
    const INTERNAL_KEYS = new Set([
      "shareBase", "shareOrigin",
      "themeBg", "themeSurface", "themeText", "themeMuted", "themeBorder",
      "themePrimary", "themePrimaryText", "themeAccent",
      "themeIntroBg", "themeIntroText",
      "themeRadius", "themeFont",
    ]);
    let url;
    if (shareBase) {
      // Rebuild the state part of the URL, EXCLUDING internal/embed-only keys
      // so the shared link doesn't recurse or leak theme overrides.
      const state = new URLSearchParams();
      params.forEach((v, k) => { if (!INTERNAL_KEYS.has(k)) state.set(k, v); });
      const sep = shareBase.includes("?") ? "&" : "?";
      const qs  = state.toString();
      url = qs ? `${shareBase}${sep}${qs}` : shareBase;
    } else if (shareOrigin) {
      try {
        const u = new URL(window.location.href);
        const parent = new URL(shareOrigin);
        u.protocol = parent.protocol;
        u.host = parent.host;
        // Drop internal-only params from the shared link.
        INTERNAL_KEYS.forEach((k) => u.searchParams.delete(k));
        url = u.toString();
      } catch { url = window.location.href; }
    } else {
      url = window.location.href;
    }
    setShareUrl(url);
    setShareOpen(true);
    try { await navigator.clipboard.writeText(url); toast.success("Link copied to clipboard"); } catch (_e) { /* clipboard blocked */ }
  };

  const captureScene = async () => {
    if (!sceneRef.current) return null;
    return toPng(sceneRef.current, {
      pixelRatio: 2, cacheBust: true,
      filter: (node) => !node.classList?.contains?.("hotspot"),
    });
  };

  const onExportPng = async () => {
    try {
      const dataUrl = await captureScene();
      if (!dataUrl) return;
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `kitchen-${doorStyle}-${colorId}.png`;
      a.click();
      toast.success("PNG downloaded");
    } catch { toast.error("Export failed"); }
  };

  const onExportPdf = async () => {
    try {
      const dataUrl = await captureScene();
      if (!dataUrl) return;
      const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: "letter" });
      const pageW = pdf.internal.pageSize.getWidth();
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(20);
      pdf.text("Your Kitchen — Cabinet Depot", 40, 40);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(10);
      pdf.setTextColor(100);
      pdf.text(`Door: ${style.label}  ·  Color: ${color.label}  ·  Layout: ${template.label}  ·  Wall: ${wall.label}`, 40, 58);
      pdf.setTextColor(0);
      const props = pdf.getImageProperties(dataUrl);
      const maxW = pageW - 80;
      const maxH = 380;
      const ratio = Math.min(maxW / props.width, maxH / props.height);
      pdf.addImage(dataUrl, "PNG", 40, 72, props.width * ratio, props.height * ratio);
      let y = 72 + props.height * ratio + 20;
      pdf.setFont("helvetica", "bold");
      pdf.text("Selected appliances", 40, y);
      y += 14;
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(10);
      template.slots.forEach((s) => {
        const sel = selections[s.id];
        if (sel && typeof sel === "object") {
          pdf.text(`${s.label.padEnd(18)}  ${(sel.brand || "").padEnd(20)}  ${(sel.name || "").padEnd(40)}  #${sel.sku || sel.id}`, 40, y);
          y += 13;
        }
      });
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9);
      pdf.setTextColor(80);
      pdf.text(`Quote appliances at ${APPLIANCE_DEPOT_URL}`, 40, y + 16);
      pdf.save(`kitchen-${doorStyle}-${colorId}.pdf`);
      toast.success("PDF downloaded");
    } catch { toast.error("PDF failed"); }
  };

  const activePicker = pickerSlot ? template.slots.find((s) => s.id === pickerSlot) : null;

  const composite = useCompositeScene({ layout: layoutId, doorStyle, color: colorId, wallColor: wallId, selections });

  // ---- Embed theming ------------------------------------------------------
  // Partner sites can tune the widget colors via URL params on `/embed`, e.g.
  //   /embed?themePrimary=%23B54934&themeAccent=%23D6A445&themeBg=%23FAF4EA
  //          &themeText=%23231F1D&themeMuted=%2380766E
  //          &themeBorder=%23E7D9C1&themeSurface=%23FFFFFF&themeRadius=6
  // Values are hex strings (`#` may be sent as `%23`); each key is optional and
  // safely ignored if malformed. When at least one param is set we inject a
  // small `<style>` block that overrides the highest-visibility Tailwind
  // surfaces so the widget picks up your brand without a code change.
  const themeCss = useMemo(() => buildThemeCss(params), [params]);

  return (
    <div data-testid={SHOWROOM.root} data-kf-themed={themeCss ? "1" : undefined} className="min-h-screen bg-[#F8FAFC] flex flex-col">
      {themeCss && <style dangerouslySetInnerHTML={{ __html: themeCss }} />}
      {!embed && <Header onReset={reset} onExportPng={onExportPng} onExportPdf={onExportPdf} onShare={share} />}
      {embed && step > 0 && <EmbedToolbar step={step} onExportPng={onExportPng} onExportPdf={onExportPdf} onShare={share} />}

      {step > 0 && step < 4 && <Stepper step={step} onGoto={setStep} />}

      {step === 0 && (
        <IntroStep onStart={() => setStep(1)} />
      )}

      {step === 1 && (
        <LayoutStep
          layoutId={layoutId} onLayout={setLayoutId}
          doorStyle={doorStyle}
          onNext={() => setStep(2)}
        />
      )}

      {step === 2 && (
        <CabinetStep
          doorStyle={doorStyle} onDoor={setDoorStyle}
          colorId={colorId}     onColor={setColorId}
          wallId={wallId}       onWall={setWallId}
          layoutId={layoutId}   style={style}
          onBack={() => setStep(1)}
          onNext={() => setStep(3)}
        />
      )}

      {step === 3 && (
        <ApplianceStep
          template={template}
          selections={selections}
          liveCatalog={liveCatalog}
          onPick={setPickerSlot}
          onRemove={removeSlot}
          onBack={() => setStep(2)}
          onNext={() => setStep(4)}
        />
      )}

      {step === 4 && (
        <main className="flex-1 min-h-0 flex flex-col relative overflow-hidden">
          <div className="px-6 py-3 border-b border-slate-200 bg-white flex items-center gap-3 shrink-0">
            <Button variant="outline" size="sm" onClick={() => setStep(3)} className="h-8 text-xs">← Change appliances</Button>
            <Button variant="ghost"   size="sm" onClick={() => setStep(1)} className="h-8 text-xs">Start over</Button>
            <div className="ml-auto text-[10px] font-mono uppercase tracking-widest text-slate-500 text-right">
              {template.caption}
            </div>
          </div>
          <KitchenScene
            sceneRef={sceneRef}
            template={template}
            style={style}
            color={color}
            wall={wall}
            selections={selections}
            onHotspot={setPickerSlot}
            onRemove={removeSlot}
            composite={composite}
          />
          <SummaryBar template={template} selections={selections} onOpen={setPickerSlot} />
        </main>
      )}

      {/* Model picker */}
      <Dialog open={!!activePicker} onOpenChange={(v) => { if (!v) { setPickerSlot(null); setBrandFilter("all"); } }}>
        <DialogContent className="max-w-4xl">
          {activePicker && (
            <>
              <DialogHeader>
                <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Choose your</div>
                <DialogTitle className="font-display text-2xl">{activePicker.label}</DialogTitle>
                <DialogDescription className="sr-only">Pick an appliance model for this slot in the kitchen scene.</DialogDescription>
              </DialogHeader>
              {(() => {
                const rawModels = (liveCatalog.slots?.[activePicker.id] || []).map(toModelCard);
                const brands = Array.from(new Set(rawModels.map((m) => m.brand).filter(Boolean))).sort();
                return brands.length > 1 ? (
                  <div className="flex items-center gap-2 flex-wrap pb-2">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mr-1">Brand</span>
                    <button
                      key="all"
                      data-testid="picker-brand-filter-all"
                      onClick={() => setBrandFilter("all")}
                      className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors whitespace-nowrap ${
                        brandFilter === "all"
                          ? "bg-slate-900 text-white border-slate-900"
                          : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"
                      }`}
                    >
                      All ({rawModels.length})
                    </button>
                    {brands.map((b) => {
                      const n = rawModels.filter((m) => m.brand === b).length;
                      const active = brandFilter === b;
                      return (
                        <button
                          key={b}
                          data-testid={`picker-brand-filter-${b.toLowerCase().replace(/\s+/g, "-")}`}
                          onClick={() => setBrandFilter(b)}
                          className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors whitespace-nowrap ${
                            active
                              ? "bg-slate-900 text-white border-slate-900"
                              : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"
                          }`}
                        >
                          {b} ({n})
                        </button>
                      );
                    })}
                  </div>
                ) : null;
              })()}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-h-[60vh] overflow-y-auto pr-1 items-start" style={{ gridAutoRows: "min-content" }}>
                {(() => {
                  // Single source of truth: Cabinet Depot's live Base44 catalog.
                  const allModels = (liveCatalog.slots?.[activePicker.id] || []).map(toModelCard);
                  const models = brandFilter === "all"
                    ? allModels
                    : allModels.filter((m) => m.brand === brandFilter);
                  if (liveCatalog.loading) {
                    return (
                      <div className="col-span-full text-center py-12 text-sm text-slate-500">
                        Loading Cabinet Depot catalog…
                      </div>
                    );
                  }
                  if (liveCatalog.error) {
                    return (
                      <div className="col-span-full text-center py-12">
                        <div className="text-sm font-semibold text-slate-900 mb-1">
                          Couldn&apos;t load the Cabinet Depot catalog
                        </div>
                        <div className="text-xs text-slate-500 max-w-md mx-auto mb-3">
                          Network or upstream hiccup. Your design is safe — tap Retry to try again.
                        </div>
                        <Button
                          data-testid="catalog-retry"
                          size="sm"
                          onClick={liveCatalog.retry}
                          className="h-8 bg-slate-900 hover:bg-slate-800 text-white text-xs"
                        >
                          <RotateCcw size={12} className="mr-1" /> Retry
                        </Button>
                      </div>
                    );
                  }
                  if (models.length === 0) {
                    return (
                      <div className="col-span-full text-center py-12">
                        <div className="text-sm font-semibold text-slate-900 mb-1">
                          No {activePicker.label.toLowerCase()} in Cabinet Depot yet
                        </div>
                        <div className="text-xs text-slate-500 max-w-md mx-auto">
                          Add a {activePicker.label.toLowerCase()} to the Cabinet Depot catalog
                          and it will appear here automatically on the next page load.
                        </div>
                      </div>
                    );
                  }
                  return models.map((m) => {
                    const sel = selections[activePicker.id];
                    const chosen = sel && (typeof sel === "object" ? sel.id === m.id : sel === m.id);
                    const slotToCategory = { fridge: "refrigerators", range: "ranges", dishwasher: "dishwashers", wine: "refrigeration" };
                    const shopUrl = safeShopUrl(m.source_url, slotToCategory, m.slot);
                    return (
                      <div key={m.id} className="relative">
                        <button
                          data-testid={SHOWROOM.modelChoice(m.id)}
                          onClick={() => chooseModel(activePicker.id, m)}
                          className={`w-full text-left rounded-lg border-2 overflow-hidden bg-white transition-all group flex flex-col ${
                            chosen ? "border-slate-900 ring-2 ring-slate-900" : "border-slate-200 hover:border-slate-500"
                          }`}
                          style={{ minHeight: 260 }}
                        >
                          <div className="relative bg-slate-50" style={{ height: 200 }}>
                            <AppliancePhoto
                              src={m.image_url || APPLIANCE_REF_IMG(m.id, m.image)}
                              fallbackSrc={APPLIANCE_REF_IMG(m.id, m.image)}
                              alt={m.name}
                              brand={m.brand}
                              className="w-full h-full object-contain p-3"
                            />                            <div className="absolute inset-x-0 bottom-0 bg-slate-900 px-3 py-2 text-white">
                              <div className="text-[9px] font-mono uppercase tracking-widest text-amber-300">{m.brand}</div>
                              <div className="text-[11px] font-bold leading-tight line-clamp-2">{m.name}</div>
                              <div className="mt-1 flex items-center justify-between gap-2">
                                <span className="text-[9px] font-mono text-slate-300 truncate">#{m.sku}</span>
                              </div>
                            </div>
                            {chosen && (
                              <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-emerald-500 text-white inline-flex items-center justify-center shadow">
                                <Check size={14} />
                              </div>
                            )}
                          </div>
                          <div className="px-3 py-2 border-t border-slate-200 flex items-center justify-between gap-2 bg-white">
                            <span className="text-[10px] text-slate-500 font-mono truncate">{m.size} · {m.finish}</span>
                            <span className="text-[10px] font-mono text-slate-400 shrink-0">{chosen ? "Selected" : "Choose"}</span>
                          </div>
                        </button>
                        {/* Deep-link overlay: opens the product page on cabinetdepot.com in a new tab */}
                        <a
                          href={shopUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`picker-shop-${m.id}`}
                          title="View on Cabinet Depot"
                          className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/90 backdrop-blur border border-slate-200 text-[10px] font-mono uppercase tracking-wider text-slate-700 hover:text-amber-700 hover:border-amber-500 shadow-sm"
                        >
                          <ExternalLink size={11} /> Shop
                        </a>
                      </div>
                    );
                  });
                })()}
              </div>
              <DialogFooter className="flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <a
                  href={APPLIANCE_DEPOT_URL} target="_blank" rel="noopener noreferrer"
                  data-testid="picker-cabinet-depot-link"
                  className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 hover:text-amber-700 mr-auto"
                >
                  Shop these at Cabinet Depot <ExternalLink size={12} />
                </a>
                {selections[activePicker.id] && (
                  <Button variant="ghost" onClick={() => { removeSlot(activePicker.id); setPickerSlot(null); }}>
                    Remove from kitchen
                  </Button>
                )}
                <Button data-testid={SHOWROOM.modelPanelClose} variant="outline" onClick={() => setPickerSlot(null)}>Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Share dialog */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">Share your kitchen</DialogTitle>
            <DialogDescription className="text-sm text-slate-600">Anyone with this link opens the exact same kitchen configuration.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input data-testid={SHOWROOM.shareLink} readOnly value={shareUrl} className="font-mono text-xs" onFocus={(e) => e.target.select()} />
              <Button
                data-testid="share-copy-button"
                onClick={async () => {
                  try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); }
                  catch { toast.error("Copy failed — press ⌘/Ctrl+C"); }
                }}
                className="bg-slate-900 hover:bg-slate-800"
              >
                Copy
              </Button>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShareOpen(false)}>Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* -------------------- Header -------------------- */
function Header({ onReset, onExportPng, onExportPdf, onShare }) {
  return (
    <header className="border-b border-slate-200 bg-white h-14 flex items-center px-6 gap-4 z-20 shrink-0">
      <Link to="/" className="inline-flex items-center gap-2 font-display font-bold text-lg tracking-tight text-slate-900">
        <span className="w-7 h-7 rounded-md bg-slate-900 text-white inline-flex items-center justify-center"><Boxes size={16} /></span>
        KitchenForge
      </Link>
      <span className="text-slate-300">/</span>
      <span className="text-xs font-mono uppercase tracking-widest text-slate-500">Kitchen Showroom</span>
      <div className="ml-auto flex items-center gap-2">
        <Button data-testid={SHOWROOM.reset} variant="ghost" size="sm" onClick={onReset} className="text-xs text-slate-600 h-8"><RotateCcw size={12} className="mr-1" /> Reset</Button>
        <Button data-testid={SHOWROOM.exportPng} variant="outline" size="sm" onClick={onExportPng} className="h-8 text-xs"><FileImage size={12} className="mr-1" /> PNG</Button>
        <Button data-testid={SHOWROOM.exportPdf} variant="outline" size="sm" onClick={onExportPdf} className="h-8 text-xs"><FileText size={12} className="mr-1" /> PDF</Button>
        <Button data-testid={SHOWROOM.share} size="sm" onClick={onShare} className="h-8 text-xs bg-slate-900 hover:bg-slate-800"><Share2 size={12} className="mr-1" /> Share</Button>
      </div>
    </header>
  );
}

// Chrome-free action bar shown in iframe/embed mode. PNG/PDF need the rendered
// scene (step 4), Share works on any step >= 1.
function EmbedToolbar({ step, onExportPng, onExportPdf, onShare }) {
  const canExport = step === 4;
  return (
    <div className="bg-white border-b border-slate-200 h-11 flex items-center px-4 gap-2 z-20 shrink-0">
      <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
        Kitchen Showroom
      </span>
      <div className="ml-auto flex items-center gap-2">
        {canExport && (
          <>
            <Button
              data-testid={SHOWROOM.exportPng}
              variant="outline"
              size="sm"
              onClick={onExportPng}
              className="h-8 text-xs"
            >
              <FileImage size={12} className="mr-1" /> PNG
            </Button>
            <Button
              data-testid={SHOWROOM.exportPdf}
              variant="outline"
              size="sm"
              onClick={onExportPdf}
              className="h-8 text-xs"
            >
              <FileText size={12} className="mr-1" /> PDF
            </Button>
          </>
        )}
        <Button
          data-testid={SHOWROOM.share}
          size="sm"
          onClick={onShare}
          className="h-8 text-xs bg-slate-900 hover:bg-slate-800"
        >
          <Share2 size={12} className="mr-1" /> Share
        </Button>
      </div>
    </div>
  );
}

/* -------------------- Intro Step (Step 0) -------------------- */
// Default hero palette — overridable via `themeIntroBg` / `themeIntroText`
// URL params (see buildThemeCss). Kept as CSS vars so the overrides don't need
// JS to apply.
const INTRO_DEFAULT_BG = "linear-gradient(135deg, #0F172A 0%, #1E293B 45%, #0F172A 100%)";
function IntroStep({ onStart }) {
  return (
    <main
      data-kf-intro="1"
      className="kf-intro-hero flex-1 flex flex-col relative overflow-hidden"
      style={{ background: `var(--kf-intro-bg, ${INTRO_DEFAULT_BG})` }}
    >
      {/* Ambient grid + halo */}
      <div className="absolute inset-0 opacity-[0.06]" style={{
        backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
        backgroundSize: "48px 48px",
      }} />
      <div className="absolute -top-24 -right-24 w-[520px] h-[520px] rounded-full blur-3xl opacity-20"
           style={{ background: "radial-gradient(circle, var(--kf-accent, #F59E0B) 0%, transparent 65%)" }} />
      <div className="absolute -bottom-40 -left-24 w-[600px] h-[600px] rounded-full blur-3xl opacity-15"
           style={{ background: "radial-gradient(circle, var(--kf-primary, #38BDF8) 0%, transparent 65%)" }} />

      <div className="relative flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-3xl text-center">
          {/* Logo — dark background so the logo's white elements pop */}
          <div className="mx-auto mb-10 inline-block bg-slate-900/70 rounded-2xl shadow-2xl px-10 py-6 backdrop-blur-md border border-white/10 ring-1 ring-white/5">
            <img
              src="/appliance-depot-logo.png"
              alt="Appliance Depot"
              className="h-16 sm:h-20 w-auto object-contain"
              draggable={false}
            />
          </div>

          {/* Kicker */}
          <div className="text-[10px] font-mono uppercase tracking-[0.35em] text-amber-300 mb-4">
            Kitchen Visualizer · Powered by Cabinet Depot
          </div>

          {/* Heading */}
          <h1 className="font-display font-black tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            Design your kitchen<br />
            <span className="text-amber-300">before you buy it.</span>
          </h1>

          <p className="text-slate-300 mt-6 max-w-xl mx-auto text-base sm:text-lg leading-relaxed">
            Pick a layout, choose Cabinet Depot cabinets in the color you love, and drop in real appliances from our live catalog — see it all come together in a photorealistic render.
          </p>

          {/* Steps preview */}
          <div className="mt-10 grid grid-cols-4 gap-3 max-w-2xl mx-auto text-left">
            {[
              { n: 1, label: "Layout",     desc: "Galley, Compact, or L-Shape" },
              { n: 2, label: "Cabinets",   desc: "5 door styles · 6 colors"    },
              { n: 3, label: "Appliances", desc: "Live Cabinet Depot inventory"},
              { n: 4, label: "Preview",    desc: "Your kitchen, rendered"      },
            ].map((s) => (
              <div key={s.n} className="rounded-lg bg-white/5 backdrop-blur-sm border border-white/10 p-3">
                <div className="w-6 h-6 rounded-full bg-amber-300 text-slate-900 inline-flex items-center justify-center text-[11px] font-black mb-2">{s.n}</div>
                <div className="text-xs font-semibold text-white">{s.label}</div>
                <div className="text-[10px] text-slate-400 mt-0.5 leading-snug">{s.desc}</div>
              </div>
            ))}
          </div>

          <div className="mt-10">
            <Button
              data-testid="intro-start"
              size="lg" onClick={onStart}
              className="bg-amber-400 hover:bg-amber-300 text-slate-900 font-bold px-8 h-12 text-base shadow-2xl shadow-amber-500/30"
            >
              Start designing →
            </Button>
            <div className="mt-4 text-[11px] font-mono uppercase tracking-widest text-slate-400">
              Free · No sign-up · ~2 min
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

/* -------------------- Wizard Stepper -------------------- */
const STEPS = [
  { n: 1, label: "Layout" },
  { n: 2, label: "Cabinets" },
  { n: 3, label: "Appliances" },
  { n: 4, label: "Your Kitchen" },
];

function Stepper({ step, onGoto }) {
  return (
    <div className="border-b border-slate-200 bg-white px-6 py-3 shrink-0">
      <div className="max-w-4xl mx-auto flex items-center gap-2">
        {STEPS.map((s, i) => {
          const active   = s.n === step;
          const complete = s.n < step;
          return (
            <div key={s.n} className="flex items-center gap-2 flex-1">
              <button
                data-testid={`stepper-${s.n}`}
                onClick={() => complete && onGoto(s.n)}
                disabled={!complete && !active}
                className={`flex items-center gap-2 min-w-0 ${complete ? "cursor-pointer" : "cursor-default"}`}
              >
                <span
                  className={`w-7 h-7 rounded-full inline-flex items-center justify-center text-xs font-bold shrink-0 transition-colors ${
                    active   ? "bg-slate-900 text-white ring-4 ring-slate-900/10"
                    : complete ? "bg-emerald-500 text-white"
                    : "bg-slate-200 text-slate-500"
                  }`}
                >
                  {complete ? <Check size={13} /> : s.n}
                </span>
                <span className={`text-xs font-semibold truncate ${active ? "text-slate-900" : complete ? "text-slate-700" : "text-slate-400"}`}>
                  {s.label}
                </span>
              </button>
              {i < STEPS.length - 1 && (
                <span className={`flex-1 h-px ${complete ? "bg-emerald-400" : "bg-slate-200"}`} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* -------------------- Step 1 — Layout -------------------- */
function LayoutStep({ layoutId, onLayout, doorStyle, onNext }) {
  return (
    <main className="flex-1 overflow-auto">
      <div className="max-w-6xl mx-auto px-6 py-10">
        <h2 className="font-display font-black text-3xl sm:text-4xl text-slate-900 tracking-tight">Pick a kitchen layout</h2>
        <p className="text-sm text-slate-600 mt-2 mb-8 max-w-2xl">Start with the shape of the room. You&apos;ll customize cabinets and appliances in the next steps.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {TEMPLATE_LIST.map((t) => {
            const active = layoutId === t.id;
            return (
              <button
                key={t.id} data-testid={`layout-${t.id}`}
                onClick={() => onLayout(t.id)}
                className={`text-left group rounded-xl overflow-hidden border-2 bg-white transition-all ${
                  active ? "border-slate-900 ring-4 ring-slate-900/10" : "border-slate-200 hover:border-slate-500"
                }`}
              >
                <div className="aspect-[16/9] bg-slate-100 relative overflow-hidden">
                  <img
                    src={SCENE_IMG(SCENE_FILE(doorStyle || "essex", t.id))}
                    alt={t.label}
                    className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform"
                  />
                  {active && (
                    <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-emerald-500 text-white inline-flex items-center justify-center shadow-lg">
                      <Check size={16} />
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <div className="text-xs font-mono uppercase tracking-widest text-amber-700 mb-1">{t.id}</div>
                  <div className="font-display font-bold text-lg text-slate-900">{t.label}</div>
                  <div className="text-xs text-slate-500 mt-1">{t.caption}</div>
                </div>
              </button>
            );
          })}
        </div>
        <div className="mt-8 flex items-center justify-end gap-3">
          <Button data-testid="step1-next" size="lg" onClick={onNext} className="bg-slate-900 hover:bg-slate-800">
            Next — Choose cabinets →
          </Button>
        </div>
      </div>
    </main>
  );
}

/* -------------------- Step 2 — Cabinets (door style + color) -------------------- */
function CabinetStep({ doorStyle, onDoor, colorId, onColor, wallId, onWall, layoutId, style, onBack, onNext }) {
  return (
    <main className="flex-1 overflow-auto">
      <div className="max-w-6xl mx-auto px-6 py-10">
        <h2 className="font-display font-black text-3xl sm:text-4xl text-slate-900 tracking-tight">Choose your cabinets</h2>
        <p className="text-sm text-slate-600 mt-2 mb-8 max-w-2xl">Pick a Cabinet Depot door style, then dial in the color and wall tint.</p>

        {/* Live preview */}
        <div className="mb-8 rounded-xl overflow-hidden border border-slate-200 bg-white aspect-[16/9] max-h-[380px]">
          <img
            src={SCENE_IMG(SCENE_FILE(doorStyle, layoutId))}
            alt={style.label}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Door Style */}
        <div className="mb-8">
          <div className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-3">Door Style</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {DOOR_STYLES.map((s) => {
              const active = doorStyle === s.id;
              return (
                <button
                  key={s.id} data-testid={SHOWROOM.cabinetChip(s.id)}
                  onClick={() => onDoor(s.id)} title={s.collection}
                  className={`group rounded-lg border-2 overflow-hidden bg-white transition-all ${
                    active ? "border-slate-900 ring-2 ring-slate-900/10" : "border-slate-200 hover:border-slate-500"
                  }`}
                >
                  <div className="aspect-square bg-slate-50 relative">
                    <img src={`${BACKEND_URL}${DOOR_SWATCH(s.id)}`} alt={s.label} className="w-full h-full object-cover" draggable={false} />
                    {active && <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-emerald-500 text-white inline-flex items-center justify-center shadow"><Check size={13} /></div>}
                  </div>
                  <div className="px-2 py-1.5 border-t border-slate-200 text-left">
                    <div className="text-[10px] font-mono uppercase tracking-wider text-amber-700">{s.collection}</div>
                    <div className="text-xs font-bold text-slate-900">{s.label}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Cabinet Color */}
        <div className="mb-8">
          <div className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-3">Cabinet Color</div>
          <div className="flex flex-wrap gap-2">
            {CABINET_COLORS.map((c) => (
              <button
                key={c.id} data-testid={`color-${c.id}`}
                onClick={() => onColor(c.id)}
                className={`flex items-center gap-2 pl-1 pr-3 py-1 rounded-md border-2 transition-all ${
                  colorId === c.id ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-700 hover:border-slate-500"
                }`}
              >
                <span className="w-7 h-7 rounded-full border border-slate-300 shrink-0" style={{ background: c.swatch }} />
                <span className="text-xs font-semibold">{c.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Wall Tint */}
        <div className="mb-8">
          <div className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-3">Wall Tint</div>
          <div className="flex flex-wrap gap-2">
            {WALL_TINTS.map((w) => (
              <button
                key={w.id} data-testid={`wall-${w.id}`}
                onClick={() => onWall(w.id)}
                title={w.label}
                className={`flex items-center gap-2 pl-1 pr-3 py-1 rounded-md border-2 transition-all ${
                  wallId === w.id ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-700 hover:border-slate-500"
                }`}
              >
                <span className="w-7 h-7 rounded-full border border-slate-300 shrink-0" style={{ background: w.swatch }} />
                <span className="text-xs font-semibold">{w.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between gap-3">
          <Button variant="outline" size="lg" onClick={onBack}>← Back</Button>
          <Button data-testid="step2-next" size="lg" onClick={onNext} className="bg-slate-900 hover:bg-slate-800">
            Next — Add appliances →
          </Button>
        </div>
      </div>
    </main>
  );
}

/* -------------------- Step 3 — Appliances -------------------- */
function ApplianceStep({ template, selections, liveCatalog, onPick, onRemove, onBack, onNext }) {
  const slotCount = template.slots.length;
  const filled    = template.slots.filter((s) => !!selections[s.id]).length;
  return (
    <main className="flex-1 overflow-auto">
      <div className="max-w-6xl mx-auto px-6 py-10">
        <h2 className="font-display font-black text-3xl sm:text-4xl text-slate-900 tracking-tight">Add appliances</h2>
        <p className="text-sm text-slate-600 mt-2 mb-2 max-w-2xl">Every appliance is pulled live from Cabinet Depot&apos;s catalog.</p>
        <p className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-8">{filled} of {slotCount} slots filled — you can skip any slot</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {template.slots.map((slot) => {
            const sel   = selections[slot.id];
            const model = sel && typeof sel === "object" ? sel : null;
            const count = (liveCatalog.slots?.[slot.id] || []).length;
            return (
              <div key={slot.id} className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                <div className="flex items-stretch">
                  <div className="w-32 h-32 bg-slate-50 flex items-center justify-center shrink-0 border-r border-slate-200 relative">
                    {model ? (
                      <AppliancePhoto
                        src={model.image_url || APPLIANCE_REF_IMG(model.id, model.image)}
                        fallbackSrc={APPLIANCE_REF_IMG(model.id, model.image)}
                        alt={model.name}
                        brand={model.brand}
                        className="w-full h-full object-contain p-2"
                      />
                    ) : (
                      <div className="text-4xl text-slate-300 font-thin">+</div>
                    )}
                  </div>
                  <div className="flex-1 p-4 min-w-0">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-amber-700">{slot.label}</div>
                    {model ? (
                      <>
                        <div className="text-sm font-bold text-slate-900 mt-1 truncate">{model.name}</div>
                        <div className="text-[10px] font-mono text-slate-500 mt-0.5">#{model.sku} · {model.brand}</div>
                        <div className="mt-3 flex items-center gap-2">
                          <Button size="sm" variant="outline" onClick={() => onPick(slot.id)} className="h-7 text-[11px]">Swap</Button>
                          <Button size="sm" variant="ghost"   onClick={() => onRemove(slot.id)} className="h-7 text-[11px] text-slate-500 hover:text-red-600">Remove</Button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="text-sm text-slate-500 mt-1">
                          {liveCatalog.loading ? "Loading Cabinet Depot…" : count > 0 ? `${count} option${count !== 1 ? "s" : ""} available` : "None in Cabinet Depot yet"}
                        </div>
                        <div className="mt-3">
                          <Button
                            data-testid={`step3-pick-${slot.id}`}
                            size="sm" onClick={() => onPick(slot.id)}
                            disabled={count === 0}
                            className="h-7 text-[11px] bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300"
                          >
                            {count === 0 ? "Not available" : `Choose ${slot.label.toLowerCase()}`}
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-8 flex items-center justify-between gap-3">
          <Button variant="outline" size="lg" onClick={onBack}>← Back</Button>
          <Button data-testid="step3-next" size="lg" onClick={onNext} className="bg-slate-900 hover:bg-slate-800">
            {filled === 0 ? "Skip and preview kitchen →" : "See my kitchen →"}
          </Button>
        </div>
      </div>
    </main>
  );
}

/* -------------------- Kitchen scene -------------------- */

/**
 * Loading overlay for the composite render. Ticks elapsed time once per second
 * (so users can see it's still working) and switches to a "taking longer than
 * usual" copy after 30s — reassures shoppers that a slow render isn't a bug
 * so they don't refresh and lose in-flight work.
 */
function CompositeLoadingOverlay({ startedAt }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const elapsed = startedAt ? Math.max(0, Math.round((now - startedAt) / 1000)) : 0;
  const slow    = elapsed >= 30;
  return (
    <div
      data-testid="composite-loading"
      className="hotspot absolute inset-0 z-40 flex flex-col items-center justify-center bg-slate-900/45 backdrop-blur-sm"
    >
      <div className="bg-white rounded-xl px-6 py-5 shadow-2xl flex items-center gap-3 max-w-sm">
        <Loader2 size={22} className="animate-spin text-blue-600 shrink-0" />
        <div>
          <div className="text-sm font-bold text-slate-900">
            Rendering your kitchen<span className="text-slate-400 font-mono ml-1">{elapsed}s</span>
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {slow
              ? "This one's taking a bit longer — hang tight, we'll retry automatically if it fails."
              : "AI is compositing your color and appliances (~15–30 s)."}
          </div>
        </div>
      </div>
    </div>
  );
}

function KitchenScene({ sceneRef, template, style, color, wall, selections, onHotspot, onRemove, composite }) {
  const wrapRef = useRef(null);
  const [dims, setDims] = useState({ w: 0, h: 0 });

  useEffect(() => {
    const compute = () => {
      const el = wrapRef.current;
      if (!el) return;
      const availW = el.clientWidth - 32;
      const availH = el.clientHeight - 32;
      const r = template.sceneRatio;
      let w = availW; let h = w / r;
      if (h > availH) { h = availH; w = h * r; }
      setDims({ w, h });
    };
    compute();
    const ro = new ResizeObserver(compute);
    if (wrapRef.current) ro.observe(wrapRef.current);
    return () => ro.disconnect();
    // `wrapRef` / `setDims` are stable; the `availW/availH/r/w/h` values are
    // local to `compute()`, and `ResizeObserver` is a browser global — none
    // of these belong in the dependency array.
  }, [template.sceneRatio]);

  const baseFile = SCENE_FILE(style.id, template.id);
  const useComposite = !!composite?.url;

  return (
    <div ref={wrapRef} className="flex-1 min-h-0 flex items-center justify-center bg-slate-100 overflow-hidden">
      <div ref={sceneRef} className="relative shadow-2xl rounded-sm overflow-hidden bg-white" style={{ width: dims.w, height: dims.h }}>
        {useComposite ? (
          <img
            src={composite.url}
            alt={`${style.label} kitchen — AI composited`}
            className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none"
            draggable={false}
            data-testid="composite-scene-img"
          />
        ) : (
          <img
            src={SCENE_IMG(baseFile)}
            alt={`${style.label} kitchen — ${template.label}`}
            className="absolute inset-0 w-full h-full object-cover pointer-events-none select-none"
            draggable={false}
          />
        )}

        {/* Appliance slots */}
        {template.slots.map((slot) => {
          const sel = selections[slot.id];
          // Selections are always live-catalog objects now (see chooseModel).
          const model = sel && typeof sel === "object" ? sel : null;
          const s = { left: `${slot.left}%`, bottom: `${slot.bottom}%`, width: `${slot.width}%`, height: `${slot.height}%` };
          if (useComposite && model) {
            return (
              <div key={slot.id} className="absolute group" style={{ ...s, zIndex: 12 }}>
                <button
                  data-testid={SHOWROOM.slotImage(slot.id)}
                  onClick={(e) => { e.stopPropagation(); onHotspot(slot.id); }}
                  title={`${model.brand} ${model.name} — click to change`}
                  className="hotspot absolute inset-0 rounded-sm hover:ring-2 hover:ring-blue-500/60 hover:bg-blue-500/5 transition-all cursor-pointer"
                />
                <button
                  onClick={(e) => { e.stopPropagation(); onRemove(slot.id); }}
                  title="Remove"
                  className="hotspot absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white border border-slate-300 text-slate-600 hover:text-red-600 hover:border-red-400 inline-flex items-center justify-center shadow opacity-0 group-hover:opacity-100 transition-opacity"
                ><X size={12} /></button>
              </div>
            );
          }
          return (
            <div key={slot.id} className="absolute" style={{ ...s, zIndex: 12 }}>
              {model ? (
                <>
                  <img
                    data-testid={SHOWROOM.slotImage(slot.id)}
                    src={model.image_url || APPLIANCE_REF_IMG(model.id, model.image)}
                    alt={model.name}
                    className="block w-full h-full drop-shadow-md"
                    style={{ objectFit: "contain", objectPosition: "center bottom", cursor: "pointer" }}
                    onClick={(e) => { e.stopPropagation(); onHotspot(slot.id); }}
                    draggable={false}
                  />
                  <button
                    onClick={(e) => { e.stopPropagation(); onRemove(slot.id); }}
                    title="Remove"
                    className="hotspot absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white border border-slate-300 text-slate-600 hover:text-red-600 hover:border-red-400 inline-flex items-center justify-center shadow"
                  ><X size={12} /></button>
                </>
              ) : (
                <EmptySlot slot={slot} onClick={() => onHotspot(slot.id)} />
              )}
            </div>
          );
        })}

        {/* Loading overlay — elapsed-time counter so users know it's still working */}
        {composite?.loading && (
          <CompositeLoadingOverlay startedAt={composite.startedAt} />
        )}

        {/* Error card — appears only after auto-retries have been exhausted */}
        {composite?.error && !composite?.loading && (
          <div
            data-testid="composite-error"
            className="hotspot absolute inset-0 z-40 flex items-center justify-center bg-slate-900/45 backdrop-blur-sm"
          >
            <div className="bg-white rounded-xl px-6 py-5 shadow-2xl max-w-md text-center">
              <div className="text-sm font-bold text-slate-900">The kitchen didn&apos;t render.</div>
              <p className="text-xs text-slate-600 mt-1 leading-snug">
                Something upstream hiccuped. Your appliance selections are still here — just tap
                Try again to re-render, or change a selection to trigger a fresh attempt.
              </p>
              <Button
                data-testid="composite-retry"
                size="sm"
                onClick={composite.retry}
                className="mt-3 h-8 bg-slate-900 hover:bg-slate-800 text-white text-xs"
              >
                <RotateCcw size={12} className="mr-1" /> Try again
              </Button>
            </div>
          </div>
        )}

        {/* Style + color badge */}
        <div className="absolute left-3 top-3 z-30 bg-white/95 backdrop-blur border border-slate-200 rounded px-2.5 py-1.5 shadow-sm">
          <div className="text-[9px] uppercase tracking-widest font-mono text-slate-500">Cabinet Depot</div>
          <div className="text-xs font-bold text-slate-900">{style.label}</div>
          <div className="text-[10px] text-slate-500 flex items-center gap-1 mt-0.5">
            <span className="inline-block w-2.5 h-2.5 rounded-full border border-slate-300" style={{ background: color.swatch }} />
            {color.label}
          </div>
        </div>

        {useComposite && (
          <div className="absolute right-3 top-3 z-30 bg-emerald-600 text-white rounded px-2 py-1 shadow-sm">
            <div className="text-[9px] uppercase tracking-widest font-mono opacity-80">Rendered</div>
            <div className="text-[10px] font-bold flex items-center gap-1">
              <Sparkles size={10} /> Photoreal {composite.cached ? "· cached" : ""}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function EmptySlot({ slot, onClick }) {
  const showLabel = slot.width >= 8;
  return (
    <button
      data-testid={SHOWROOM.hotspot(slot.id)}
      onClick={onClick} title={`Pick ${slot.label}`}
      className="hotspot group absolute inset-0 flex items-center justify-center rounded-sm border-2 border-dashed border-blue-500/70 bg-blue-500/5 hover:bg-blue-500/10 transition-colors"
    >
      <span className="relative flex items-center justify-center">
        <span className="absolute w-9 h-9 rounded-full bg-blue-500/30 animate-ping" />
        <span className="relative w-9 h-9 rounded-full bg-blue-600 text-white inline-flex items-center justify-center shadow-lg"><Plus size={16} /></span>
      </span>
      {showLabel && (
        <span className="absolute bottom-2 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] font-mono uppercase tracking-widest text-blue-800 bg-white/90 px-1.5 py-0.5 rounded border border-blue-200">
          Pick {slot.label}
        </span>
      )}
    </button>
  );
}

/* -------------------- Summary bar -------------------- */
function SummaryBar({ template, selections, onOpen }) {
  const count = Object.keys(selections).length;
  return (
    <div className="border-t border-slate-200 bg-white px-6 py-3 flex items-center gap-4 shrink-0 overflow-x-auto">
      <div>
        <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Your kitchen</div>
        <div className="text-sm font-semibold text-slate-900">{count} appliance{count !== 1 ? "s" : ""} selected</div>
      </div>
      <div className="flex-1 flex items-center gap-2 min-w-0 overflow-x-auto thin-scroll">
        {template.slots.filter((s) => selections[s.id]).map((s) => {
          const sel = selections[s.id];
          const m = typeof sel === "object" ? sel : null;
          if (!m) return null;
          // Deep-link the appliance chip to Cabinet Depot: prefer the
          // per-product document URL from Base44 (m.source_url), fall back to
          // the appliance-category page on cabinetdepot.com.
          const slotToCategory = { fridge: "refrigerators", range: "ranges", dishwasher: "dishwashers", wine: "refrigeration" };
          const shopUrl = safeShopUrl(m.source_url, slotToCategory, m.slot);
          return (
            <div
              key={s.id}
              className="shrink-0 flex items-center gap-2 px-2 py-1.5 rounded-md border border-slate-200 hover:border-slate-500 bg-white transition-colors group"
            >
              <button
                data-testid={SHOWROOM.summaryItem(s.id)}
                onClick={() => onOpen(s.id)}
                title="Click to change this appliance"
                className="flex items-center gap-2"
              >
                <AppliancePhoto
                  src={m.image_url || APPLIANCE_REF_IMG(m.id, m.image)}
                  fallbackSrc={APPLIANCE_REF_IMG(m.id, m.image)}
                  alt={m.name}
                  brand={m.brand}
                  className="w-9 h-9 object-contain"
                />
                <div className="text-left">
                  <div className="text-[10px] font-mono uppercase tracking-wider text-amber-700">{m.brand}</div>
                  <div className="text-xs font-semibold text-slate-900 truncate max-w-[160px]">{m.name}</div>
                  <div className="text-[9px] font-mono text-slate-500">#{m.sku}</div>
                </div>
              </button>
              <a
                href={shopUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`summary-shop-${s.id}`}
                title="View on Cabinet Depot"
                onClick={(e) => e.stopPropagation()}
                className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-md text-slate-400 hover:text-amber-700 hover:bg-amber-50"
              >
                <ExternalLink size={13} />
              </a>
            </div>
          );
        })}
        {count === 0 && (
          <div className="text-xs text-slate-500 font-mono flex items-center gap-1.5">
            <Sparkles size={12} className="text-amber-500" /> Click any blue hotspot in the kitchen above to start
          </div>
        )}
      </div>
    </div>
  );
}
